import * as React from "react";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Slider from "@mui/material/Slider";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";

import PauseRounded from "@mui/icons-material/PauseRounded";
import PlayArrowRounded from "@mui/icons-material/PlayArrowRounded";
import FastForwardRounded from "@mui/icons-material/FastForwardRounded";
import FastRewindRounded from "@mui/icons-material/FastRewindRounded";
import VolumeUpRounded from "@mui/icons-material/VolumeUpRounded";
import VolumeDownRounded from "@mui/icons-material/VolumeDownRounded";

import { useMusic } from "./MusicContext";
import m1 from "../assets/images/cargo.jpeg";

/* ---------- STYLES ---------- */

const Widget = styled("div")({
  padding: 18,
  borderRadius: 20,
  width: 340,
  margin: "auto",
  color: "whitesmoke",       // text color
  background:
    "linear-gradient(135deg, rgba(255,255,255,0.05), rgba(255,255,255,0.1))", 
  backdropFilter: "blur(40px)",
  boxShadow: `
    0 20px 40px rgba(0,0,0,0.5),
    inset 0 0 20px rgba(255,255,255,0.1)
  `,
  border: "1px solid rgba(255,255,255,0.2)",
  "& strong": {
    color: "wheat",            
    fontSize: 16,
  },
  "& small": {
    color: "whitesmoke",       
    opacity: 0.7,
  },
});


const CoverImage = styled("div")({
  width: 110,
  height: 110,
  borderRadius: 14,
  overflow: "hidden",
  margin: "auto",
  boxShadow: "0 15px 30px rgba(0,0,0,0.4)",
  "& img": {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
});

const NeonSlider = styled(Slider)({
  color: "#00f5ff",
  "& .MuiSlider-thumb": {
    boxShadow: "0 0 12px rgba(0,245,255,.9)",
  },
  "& .MuiSlider-track": {
    background:
      "linear-gradient(90deg, #00f5ff, #8a2be2)",
  },
});

/* ---------- COMPONENT ---------- */

export default function Music() {
  const {
    playing,
    play,
    pause,
    next,
    prev,
    duration,
    currentTime,
    seek,
    volume,
    changeVolume,
    currentSong,
  } = useMusic();

  const format = (s = 0) =>
    `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

  return (
    <Box sx={{ p: 3 }}>
      <Widget>
        {/* COVER */}
        <CoverImage>
          <img src={m1} alt="music-cover" />
        </CoverImage>

        {/* TITLE */}
        <Box sx={{ textAlign: "center", mt: 1 }}>
          <strong style={{ fontSize: 16 }}>
            {currentSong.title}
          </strong>
          <br />
          <small style={{ opacity: 0.7 }}>
            {currentSong.artist}
          </small>
        </Box>

        {/* PROGRESS */}
        <NeonSlider
          value={currentTime}
          min={0}
          max={duration || 0}
          onChange={(_, v) => seek(v)}
          sx={{ mt: 2 }}
        />

        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <small>{format(currentTime)}</small>
          <small>-{format(duration - currentTime)}</small>
        </Box>

        {/* CONTROLS */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            mt: 1,
            gap: 1,
          }}
        >
          <IconButton sx={{ color: "whitesmoke" }} onClick={prev}>
            <FastRewindRounded fontSize="large" />
          </IconButton>

          <IconButton
            onClick={playing ? pause : play}
            sx={{
              background:
                "linear-gradient(135deg,#00f5ff,#8a2be2)",
              color: "#000",
              boxShadow: "0 0 25px rgba(0,245,255,.8)",
              "&:hover": {
                transform: "scale(1.1)",
              },
            }}
          >
            {playing ? (
              <PauseRounded sx={{ fontSize: 46 }} />
            ) : (
              <PlayArrowRounded sx={{ fontSize: 46 }} />
            )}
          </IconButton>

          <IconButton sx={{ color: "whitesmoke" }} onClick={next}>
            <FastForwardRounded fontSize="large" />
          </IconButton>
        </Box>

        {/* VOLUME */}
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 1 }}>
          <VolumeDownRounded />
          <NeonSlider
            value={volume * 100}
            onChange={(_, v) => changeVolume(v / 100)}
          />
          <VolumeUpRounded />
        </Stack>
      </Widget>
    </Box>
  );
}
